// src/api/services/loan-information.service.ts
import axiosInstance from "../api.axiosInstance"
import type {
  CreateAccountResponseDto,
  LightReachDisclosureDto,
  LightReachPricingResponse,
  LightReachCreditApplicationRequestDto,
  LightReachCreditApplicationResponseDto,
  LightReachQuoteDto,
  LightReachApplicationResponseDto,
  LightReachContractListDto,
  LightReachValidationResponse,
  LightReachAvailableAddersDto,
} from "../dto/light-reach.dto"

export class LightReachService {
  async getPricingDetails(
    proposalId: string,
  ): Promise<LightReachPricingResponse> {
    try {
      const response = await axiosInstance.get(
        `light-reach/v1/${proposalId}/get-pricing`,
      )
      return response.data
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorType = errorData?.errorType || errorData?.error
      const errorMessage = errorData?.message || error.message

      // Check for specific error types
      if (errorType === `LIGHTREACH_LICENSE_ERROR`) {
        // Pass through the full error object so the component can check the type
        const err = new Error(errorMessage) as any
        err.errorType = errorType
        throw err
      }

      if (errorType === `LIGHTREACH_AVAILABLE_ADDERS_ERROR`) {
        // Pass through the full error object so the component can check the type
        const err = new Error(errorMessage) as any
        err.errorType = errorType
        throw err
      }

      // Vision validation (e.g. invalid/placeholder HO email) — show server message as-is
      if (
        errorType === `ValidationFailed` ||
        errorData?.error === `ValidationFailed`
      ) {
        throw new Error(errorMessage)
      }

      if (error.response?.status === 400) {
        throw new Error(`Invalid request parameters: ${errorMessage}`)
      }
      if (error.response?.status === 404) {
        throw new Error(`Loan provider not found`)
      }
      // Handle ExternalServiceFailed (424 Failed Dependency) - pass through the detailed message
      if (error.response?.status === 424) {
        throw new Error(errorMessage)
      }
      if (error.response?.status === 500) {
        throw new Error(errorData.error || errorMessage)
      }
      throw error
    }
  }
  async createAccount(proposalId: string): Promise<CreateAccountResponseDto> {
    try {
      const response = await axiosInstance.post(
        `light-reach/v1/create-account`,
        {
          proposalId,
        },
      )
      return response.data
    } catch (error: any) {
      const errorData = error.response?.data
      const errorType = errorData?.errorType || errorData?.error
      const errorMessage = errorData?.message || error.message

      if (
        errorType === `ValidationFailed` ||
        errorData?.error === `ValidationFailed`
      ) {
        throw new Error(errorMessage)
      }

      if (error.response?.status === 400) {
        throw new Error(`Invalid request parameters: ${errorMessage}`)
      }
      if (error.response?.status === 404) {
        throw new Error(`Proposal not found`)
      }
      if (error.response?.status === 500) {
        throw new Error(
          `Failed to create account: ${
            error.response.data?.message || error.message
          }`,
        )
      }
      throw error
    }
  }

  async createSystemDesign(proposalId: string): Promise<void> {
    try {
      await axiosInstance.post(`light-reach/v1/create-system-design`, {
        proposalId,
      })
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(
          `Invalid request parameters: ${
            error.response.data?.message || error.message
          }`,
        )
      }
      if (error.response?.status === 404) {
        throw new Error(`Proposal or LightReach account not found`)
      }
      if (error.response?.status === 500) {
        throw new Error(
          `Failed to create system design: ${
            error.response.data?.message || error.message
          }`,
        )
      }
      throw error
    }
  }

  async getDisclosures(): Promise<LightReachDisclosureDto[]> {
    try {
      const response = await axiosInstance.get(`light-reach/v1/disclosures`)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 500) {
        throw new Error(
          `Failed to fetch disclosures: ${
            error.response.data?.message || error.message
          }`,
        )
      }
      throw error
    }
  }

  async submitCreditApplication(
    proposalId: string,
    applicationData: LightReachCreditApplicationRequestDto,
  ): Promise<LightReachCreditApplicationResponseDto> {
    try {
      const response = await axiosInstance.post(
        `light-reach/v1/proposal/${proposalId}/credit-application`,
        applicationData,
      )
      return response.data
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 400) {
        throw new Error(`Invalid application data: ${errorMessage}`)
      }
      if (error.response?.status === 404) {
        throw new Error(`Proposal or LightReach account not found`)
      }
      if (error.response?.status === 422) {
        throw new Error(`Application validation failed: ${errorMessage}`)
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to submit application: ${errorMessage}`)
      }
      throw error
    }
  }

  async getQuote(
    proposalId: string,
    costPerWatt?: number,
  ): Promise<LightReachQuoteDto | null> {
    try {
      const params = costPerWatt ? { costPerWatt: costPerWatt.toString() } : {}
      const response = await axiosInstance.get(
        `light-reach/v1/proposal/${proposalId}/quote`,
        { params },
      )
      return response.data
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 404) {
        // Return null if no quote found (not an error case)
        return null
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to fetch quotes: ${errorMessage}`)
      }
      throw error
    }
  }

  async getApplication(
    proposalId: string,
  ): Promise<LightReachApplicationResponseDto | null> {
    try {
      const response = await axiosInstance.get(
        `light-reach/v1/proposal/${proposalId}/application`,
      )
      return response.data
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 404) {
        // Return null if no application found (not an error case)
        return null
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to fetch application: ${errorMessage}`)
      }
      throw error
    }
  }

  async sendContract(proposalId: string): Promise<LightReachContractListDto> {
    try {
      const response = await axiosInstance.post(
        `light-reach/v1/proposal/${proposalId}/send-contract`,
        {},
        {
          timeout: 30000, // 30 seconds for contract sending
        },
      )
      return response.data
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 400) {
        throw new Error(`Invalid request parameters: ${errorMessage}`)
      }
      if (error.response?.status === 404) {
        throw new Error(`Proposal or LightReach account not found`)
      }
      if (error.response?.status === 502) {
        throw new Error(`LightReach service unavailable: ${errorMessage}`)
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to send contract: ${errorMessage}`)
      }
      throw error
    }
  }

  async getContracts(
    proposalId: string,
  ): Promise<LightReachContractListDto | null> {
    try {
      const response = await axiosInstance.get(
        `light-reach/v1/proposal/${proposalId}/contracts`,
      )
      return response.data
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 404) {
        // Return null if no contract found
        return null
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to fetch contracts: ${errorMessage}`)
      }
      throw error
    }
  }

  async deleteApplication(applicationId: string): Promise<void> {
    try {
      await axiosInstance.delete(`light-reach/v1/application/${applicationId}`)
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 404) {
        throw new Error(`Application not found`)
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to delete application: ${errorMessage}`)
      }
      throw error
    }
  }

  async deleteContract(proposalId: string): Promise<void> {
    try {
      await axiosInstance.delete(
        `light-reach/v1/proposal/${proposalId}/contracts`,
      )
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 404) {
        throw new Error(`Contract not found`)
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to delete contract: ${errorMessage}`)
      }
      throw error
    }
  }

  async validateChangeOrderNeeded(
    proposalId: string,
    production?: number,
  ): Promise<LightReachValidationResponse | null> {
    try {
      const params = production ? { production: production.toString() } : {}
      const response = await axiosInstance.get(
        `light-reach/v1/proposal/${proposalId}/validate-change-order`,
        { params },
      )
      return response.data
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 404) {
        // Return null if no LightReach account found
        return null
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to validate change order: ${errorMessage}`)
      }
      throw error
    }
  }

  async getAvailableAdders(
    proposalId: string,
  ): Promise<LightReachAvailableAddersDto | null> {
    try {
      const response = await axiosInstance.get(
        `light-reach/v1/proposal/${proposalId}/available-adders`,
      )
      return response.data
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 404) {
        // Return null if no LightReach account found
        return null
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to fetch available adders: ${errorMessage}`)
      }
      throw error
    }
  }

  async voidQuote(proposalId: string): Promise<void> {
    try {
      await axiosInstance.delete(`light-reach/v1/proposal/${proposalId}/quote`)
    } catch (error: any) {
      // Handle structured error responses from backend
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message

      if (error.response?.status === 400) {
        throw new Error(`Invalid quote update data: ${errorMessage}`)
      }
      if (error.response?.status === 404) {
        throw new Error(`Quote not found`)
      }
      if (error.response?.status === 500) {
        throw new Error(`Failed to update quote: ${errorMessage}`)
      }
      throw error
    }
  }
}

export const lightReachService = new LightReachService()
